"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import type { SessionDetail } from "@/lib/api";
import {
  CASCADE_LANES,
  CASCADE_LEGEND,
  DEGRADE_COLOR,
  DIRECT_LANES,
  DIRECT_LEGEND,
  LANE_FILL,
  LIVEAVATAR_LANES,
  LIVEAVATAR_LEGEND,
  RECOVERY_COLOR,
  barClassName,
  buildDirectSpansFromEvents,
  buildDirectSpansFromTurns,
  buildLiveAvatarSpansFromEvents,
  buildLiveAvatarSpansFromTurns,
  buildLpSpans,
  buildSpansFromEvents,
  buildSpansFromTurns,
  chipColor,
  draftLabel,
  formatSpanTiming,
  kindTitle,
  laneHeading,
  resolveTimelineMode,
  type PipelineEvent,
  type Span,
  type TimelineMode,
} from "@/lib/pipeline-timeline";

type PopoverState = {
  span: Span;
  left: number;
  top: number;
  place: "below" | "above";
};

async function loadEvents(url?: string | null): Promise<PipelineEvent[]> {
  if (!url) return [];
  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    const text = await res.text();
    const out: PipelineEvent[] = [];
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        out.push(JSON.parse(trimmed) as PipelineEvent);
      } catch {
        /* skip bad line */
      }
    }
    return out;
  } catch {
    return [];
  }
}

export function PipelineTimelineModal({
  detail,
  onClose,
}: {
  detail: SessionDetail;
  onClose: () => void;
}) {
  const [eventSpans, setEventSpans] = useState<Span[] | null>(null);
  const [timelineMode, setTimelineMode] = useState<TimelineMode>(
    resolveTimelineMode(detail, []),
  );
  const [loadingEvents, setLoadingEvents] = useState(
    Boolean(detail.eventsUrl || detail.livePerceptionEventsUrl),
  );
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!detail.eventsUrl && !detail.livePerceptionEventsUrl) {
        setEventSpans(null);
        setTimelineMode(resolveTimelineMode(detail, []));
        setLoadingEvents(false);
        return;
      }
      setLoadingEvents(true);
      setEventSpans(null);
      const [events, lpRaw] = await Promise.all([
        loadEvents(detail.eventsUrl),
        loadEvents(detail.livePerceptionEventsUrl),
      ]);
      if (cancelled) return;
      const mode = resolveTimelineMode(detail, events);
      setTimelineMode(mode);
      const convo =
        mode === "liveavatar"
          ? (buildLiveAvatarSpansFromEvents(events, detail) || [])
          : mode === "direct"
            ? (buildDirectSpansFromEvents(events, detail) || [])
            : (buildSpansFromEvents(events, detail) || []);
      const lp = mode === "cascade" || mode === "direct" || mode === "liveavatar"
        ? buildLpSpans(lpRaw as Array<Record<string, unknown>>)
        : [];
      setEventSpans([...convo, ...lp]);
      setLoadingEvents(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [detail]);

  const spans = useMemo(() => {
    if (loadingEvents) return [];
    if (eventSpans) return eventSpans;
    if (timelineMode === "liveavatar") return buildLiveAvatarSpansFromTurns(detail);
    if (timelineMode === "direct") return buildDirectSpansFromTurns(detail);
    return buildSpansFromTurns(detail);
  }, [loadingEvents, eventSpans, detail, timelineMode]);
  const maxMs = Math.max(
    1000,
    ...spans.map((s) => s.endMs),
    Number(detail.durationSec || 1) * 1000,
  );
  const lanes =
    timelineMode === "liveavatar"
      ? LIVEAVATAR_LANES
      : timelineMode === "direct"
        ? DIRECT_LANES
        : CASCADE_LANES;
  const legend =
    timelineMode === "liveavatar"
      ? LIVEAVATAR_LEGEND
      : timelineMode === "direct"
        ? DIRECT_LEGEND
        : CASCADE_LEGEND;
  const sourceNote = loadingEvents
    ? "Loading session events…"
    : eventSpans
      ? "From session events."
      : detail.eventsUrl
        ? "Events unavailable — showing turns."
        : "From session turns.";

  function placePopover(span: Span, el: HTMLElement) {
    const modal = modalRef.current;
    if (!modal) {
      setPopover({ span, left: 24, top: 24, place: "below" });
      return;
    }
    const mr = modal.getBoundingClientRect();
    const br = el.getBoundingClientRect();
    const popW = 320;
    const popH = 180;
    let left = br.left - mr.left + br.width / 2 + modal.scrollLeft;
    left = Math.min(Math.max(left, popW / 2 + 12), modal.clientWidth - popW / 2 - 12);
    const spaceBelow = mr.bottom - br.bottom;
    const place: "below" | "above" = spaceBelow < popH + 16 ? "above" : "below";
    const top =
      place === "below"
        ? br.bottom - mr.top + 10 + modal.scrollTop
        : br.top - mr.top - 10 + modal.scrollTop;
    setPopover({ span, left, top, place });
  }

  function onBarClick(span: Span, e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    placePopover(span, e.currentTarget);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if ((span.lane === "tts" || span.lane === "stt") && span.audioUrl) {
      const a = new Audio(span.audioUrl);
      audioRef.current = a;
      void a.play().catch(() => undefined);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={modalRef}
        className="modal card pipeline-modal"
        role="dialog"
        aria-label="Pipeline timeline"
        onClick={(e) => {
          e.stopPropagation();
          setPopover(null);
        }}
      >
        <header className="pipeline-modal-head">
          <div>
            <h2>Pipeline timeline</h2>
            <p>
              {timelineMode === "liveavatar"
                ? "LiveAvatar FULL — transcript chips only (ASR/LLM/TTS run on LiveAvatar, not Pulse). No Cascade STT/LLM/TTS lanes. "
                : timelineMode === "direct"
                  ? "Mouth transcript chips, Client handoffs (In / Outcome / Income), Live Perception. Click a chip or bar for text and timing. "
                  : "Swimlanes for STT / LLM / TTS / Live Perception. Click a bar for draft text, timing, and turn audio. "}
              {sourceNote}
            </p>
          </div>
          <button type="button" className="btn icon" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className={`pipeline-lanes${loadingEvents ? " is-loading" : ""}`} aria-busy={loadingEvents}>
          {!loadingEvents && timelineMode === "cascade" ? (
            <div className="pipeline-cross-links" aria-hidden="true">
              <svg viewBox="0 0 100 100" preserveAspectRatio="none">
                {(() => {
                  const byId = new Map(spans.map((s) => [s.id, s]));
                  const laneY = (lane: Span["lane"]) => {
                    const idx = lanes.indexOf(lane);
                    if (idx < 0) return 50;
                    return ((idx + 0.5) / lanes.length) * 100;
                  };
                  const paths: ReactNode[] = [];
                  for (const from of spans) {
                    if (from.kind === "spec-discarded" && from.linkedToId) {
                      const to = byId.get(from.linkedToId);
                      if (!to) continue;
                      const x1 = (from.endMs / maxMs) * 100;
                      const x2 = (to.startMs / maxMs) * 100;
                      const y = laneY("llm");
                      const mid = (x1 + x2) / 2;
                      const lift = Math.min(6, Math.abs(x2 - x1) * 0.35 + 2);
                      paths.push(
                        <path
                          key={`spec-${from.id}`}
                          className="pipeline-link spec-link"
                          d={`M ${x1} ${y} C ${mid} ${y - lift}, ${mid} ${y - lift}, ${x2} ${y}`}
                          fill="none"
                        />,
                      );
                    }
                    if (from.lane === "stt" && from.drivesRegenId) {
                      const to = byId.get(from.drivesRegenId);
                      if (!to) continue;
                      const x1 = (from.endMs / maxMs) * 100;
                      const x2 = (to.startMs / maxMs) * 100;
                      const y1 = laneY("stt");
                      const y2 = laneY("llm");
                      const midX = (x1 + x2) / 2;
                      const midY = (y1 + y2) / 2;
                      paths.push(
                        <path
                          key={`stt-${from.id}`}
                          className="pipeline-link stt-link"
                          d={`M ${x1} ${y1} C ${midX} ${midY}, ${midX} ${midY}, ${x2} ${y2}`}
                          fill="none"
                        />,
                      );
                    }
                  }
                  return paths;
                })()}
              </svg>
            </div>
          ) : null}
          {lanes.map((lane) => {
            const laneSpans = spans.filter((s) => s.lane === lane);
            return (
            <div key={lane} className="pipeline-lane">
              <div className="pipeline-lane-label">
                {laneHeading(lane, timelineMode)}
              </div>
              <div
                className={`pipeline-track${lane === "quality" ? " is-quality" : ""}`}
                style={{ background: LANE_FILL[lane] }}
              >
                {loadingEvents ? <div className="pipeline-track-skeleton" /> : null}
                {!loadingEvents &&
                  laneSpans.map((s, idx) => {
                    if (lane === "quality") {
                      const leftPct = Math.max(0, Math.min(100, (s.startMs / maxMs) * 100));
                      const overlap = laneSpans
                        .slice(0, idx)
                        .filter((o) => Math.abs(o.startMs - s.startMs) < 120).length;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className={`pipeline-marker${s.kind === "recovery" ? " recovery" : " degrade"}`}
                          aria-label={s.label || "quality marker"}
                          title={s.label}
                          style={{
                            left: `calc(${leftPct}% + ${overlap * 10}px)`,
                            background: s.kind === "recovery" ? RECOVERY_COLOR : DEGRADE_COLOR,
                          }}
                          onClick={(e) => onBarClick(s, e)}
                        />
                      );
                    }
                    const left = `${(s.startMs / maxMs) * 100}%`;
                    const width = `${Math.max(s.display === "chip" ? 3.2 : 1.2, ((s.endMs - s.startMs) / maxMs) * 100)}%`;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={barClassName(s)}
                        style={{
                          left,
                          width,
                          background: chipColor(s, timelineMode),
                        }}
                        aria-label={`${lane} ${s.label || s.kind || "span"}`}
                        title={s.label}
                        onClick={(e) => onBarClick(s, e)}
                      >
                        {s.display === "chip" ? s.label : null}
                      </button>
                    );
                  })}
              </div>
            </div>
            );
          })}
        </div>

        <div className="pipeline-legend" aria-label="Legend">
          {legend.map((item) => (
            <span
              key={item.label}
              className={`pipeline-legend-chip${item.className ? ` ${item.className}` : ""}`}
              style={{ backgroundColor: item.color }}
            >
              {item.label}
            </span>
          ))}
        </div>

        {popover ? (
          <div
            className={`pipeline-popover place-${popover.place}`}
            style={{ left: popover.left, top: popover.top }}
            role="dialog"
            aria-label="Span details"
            onClick={(e) => e.stopPropagation()}
          >
            <header>
              <b>
                {popover.span.lane.toUpperCase()} · {kindTitle(popover.span)}
              </b>
              <button
                type="button"
                className="pipeline-popover-close"
                aria-label="Close details"
                onClick={() => setPopover(null)}
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </header>
            <div className="pipeline-popover-meta">
              {(() => {
                const t = formatSpanTiming(popover.span.startMs, popover.span.endMs);
                return (
                  <>
                    <span className="pipeline-popover-range">{t.range}</span>
                    {t.duration ? (
                      <span className="pipeline-popover-dur"> · {t.duration}</span>
                    ) : (
                      <span className="pipeline-popover-dur"> · instant</span>
                    )}
                  </>
                );
              })()}
              {popover.span.delegationId ? ` · ${popover.span.delegationId}` : ""}
              {popover.span.channel ? ` · ${popover.span.channel}` : ""}
              {popover.span.provider ? ` · ${popover.span.provider}` : ""}
              {popover.span.model ? ` · ${popover.span.model}` : ""}
              {popover.span.linkedToId ? " · linked → regen" : ""}
              {popover.span.linkedFromId ? " · from discarded spec" : ""}
              {popover.span.drivesRegenId ? " · drives regen" : ""}
              {popover.span.drivenBySttId ? " · from STT" : ""}
            </div>
            {popover.span.prompt ? (
              <div className="pipeline-popover-block">
                <span>Prompt</span>
                <p>{popover.span.prompt}</p>
              </div>
            ) : null}
            {popover.span.frameUrl ? (
              <img
                src={popover.span.frameUrl}
                alt=""
                className="pipeline-lp-frame"
              />
            ) : null}
            {popover.span.text ? (
              <div className="pipeline-popover-block">
                <span>{draftLabel(popover.span)}</span>
                <p>{popover.span.text}</p>
              </div>
            ) : (
              <p className="pipeline-popover-empty">
                {popover.span.audioUrl
                  ? "Playing turn audio…"
                  : "No text captured for this span"}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
